//* This file contains the API utility object for managing CRUD operations for teams.
import { API_URI } from "../../constants/consts";
import { api } from "../../services/apis/configs/axiosConfigs";

//* Type for pagination data
import { type Pagination } from "../../services/stores/types";

//* Importing types from the teams module
import { Team, TeamResponse } from "./types/types";

/**
 * TeamAPI is an API utility object to manage CRUD operations (create, read, update, delete)
 * for teams through HTTP requests. It interacts with a backend API and handles operations
 * such as fetching a list of teams, retrieving a team by ID, creating, updating, and deleting teams.
 */
export const teamAPI = {
  /**
   * Fetches a paginated list of teams from the API.
   *
   * @param {string} search - Optional search term to filter teams by name (default is an empty string).
   * @param {number} page - Page number for pagination (default is 1).
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {number} perPage - Number of teams per page (default is 10).
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<TeamResponse>} - Returns a paginated response containing teams and pagination info.
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<TeamResponse> {
    const searchQuery = search ? `&search=${search}` : ""; // Add search query if provided
    const url = `${API_URI}/admin/private/teams?page=${page}&perPage=${perPage}${searchQuery}`; // API endpoint for fetching teams

    return api
      .request({
        url: url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          teams: data.teams || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching teams:", error);
        throw new Error("Failed to fetch teams");
      });
  },

  /**
   * Fetches a specific team by its ID from the API.
   *
   * @param {string} id - UUID of the team to fetch.
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<Team>} - Returns the team data if found.
   *
   * @throws {Error} - Throws an error if the team is not found or if the response is invalid.
   */
  getById: async function (
    id: string,
    token: string,
    cancel = false
  ): Promise<Team> {
    return api
      .request({
        url: `${API_URI}/admin/private/teams/${id}`, // API endpoint for fetching a team by ID
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.data && response.data.result) {
          return response.data.data; // Return the team data if found
        } else {
          throw new Error("Team not found or invalid response.");
        }
      })
      .catch((error) => {
        console.error("Error fetching team:", error); // Log for debugging
        throw new Error("Failed to fetch team");
      });
  },

  /**
   * Creates a new team by sending a POST request to the API.
   *
   * @param {Object} data - Data for the new team, including name and description.
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<Team>} - Returns the created team data.
   *
   * @throws {Error} - Throws an error if the team creation fails.
   */
  create: async function (
    data: {
      name: string;
      is_first_semester: boolean;
      shift: string;
      start_date: Date;
      end_date: Date;
      unit_id: string;
      course_id: string;
    },
    token: string,
    cancel = false
  ): Promise<Team> {
    return api
      .request({
        url: `${API_URI}/admin/private/teams`, // API endpoint for creating a team
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data, // Team data to be sent in the request body
      })
      .then((response) => response.data.data) // Return the response data (created team)
      .catch((error) => {
        console.error("Error creating team:", error); // Log for debugging
        throw new Error("Failed to create team");
      });
  },

  /**
   * Updates an existing team by sending a PUT request to the API.
   *
   * @param {string} id - UUID of the team to update.
   * @param {Object} data - Updated team data including name and description.
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<Team>} - Returns the updated team data.
   *
   * @throws {Error} - Throws an error if the team update fails.
   */
  update: async function (
    id: string,
    data: {
      name: string;
      is_first_semester: boolean;
      shift: string;
      start_date: Date;
      end_date: Date;
      unit_id: string;
      course_id: string;
    },
    token: string,
    cancel = false
  ): Promise<Team> {
    return api
      .request({
        url: `${API_URI}/admin/private/teams/${id}`, // API endpoint for updating a team
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data, // Updated team data to be sent in the request body
      })
      .then((response) => response.data.data) // Return the updated team data
      .catch((error) => {
        console.error("Error updating team:", error); // Log for debugging
        throw new Error("Failed to update team");
      });
  },

  /**
   * Deletes an existing team by sending a DELETE request to the API.
   *
   * @param {string} id - UUID of the team to delete.
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<any>} - Returns the result of the deletion.
   *
   * @throws {Error} - Throws an error if the deletion fails.
   */
  delete: async function (id: string, token: string, cancel = false) {
    return api
      .request({
        url: `${API_URI}/admin/private/teams/${id}`, // API endpoint for deleting a team
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data) // Return the response data (deletion result)
      .catch((error) => {
        console.error("Error deleting team:", error); // Log for debugging
        throw new Error("Failed to delete team");
      });
  },
};
